import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ChecklistDownloadForm } from "@/components/calv/ChecklistDownloadForm";
import { SITE_URL } from "@/lib/locale";
import { jsonLdScript, breadcrumbSchema } from "@/lib/seo";

const TITLE = "Performance Marketing Checklist for SMBs — Calvarius";
const DESC =
  "Methodical checklist in seven phases — economic pre-validation, tracking architecture, pacing, conversion optimization, reporting cadence. Free PDF, 20 pages.";
const URL_ = `${SITE_URL}/en/tools/performance-marketing-checklist`;
const ALT = `${SITE_URL}/tools/performance-marketing-checkliste`;

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Performance Marketing Checklist for SMBs",
  description: DESC,
  author: { "@type": "Person", name: "André Freier" },
  publisher: { "@type": "Organization", name: "Calvarius GmbH" },
  inLanguage: "en-US",
  url: URL_,
  about: "Performance marketing setup for small and medium-sized businesses",
};

export const Route = createFileRoute("/en/tools/performance-marketing-checklist")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_ },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "de_DE" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [
      { rel: "canonical", href: URL_ },
      { rel: "alternate", hrefLang: "en", href: URL_ },
      { rel: "alternate", hrefLang: "de", href: ALT },
      { rel: "alternate", hrefLang: "x-default", href: ALT },
    ],
    scripts: [
      jsonLdScript(
        breadcrumbSchema([
          { name: "Start", path: "/en" },
          { name: "Tools", path: "/en/tools" },
          { name: "Performance Marketing Checklist", path: "/en/tools/performance-marketing-checklist" },
        ]),
      ),
      jsonLdScript(articleSchema),
    ],
  }),
  component: ChecklistEN,
});

const PHASES = [
  { n: "01", t: "Economic pre-validation", d: "AOV, margin, and max-CPC formula. When performance marketing is not the right lever — and which pre-questions carry the decision." },
  { n: "02", t: "Tracking and data architecture", d: "Set up conversion tracking cleanly: server-side, Consent Mode v2, first-party data, offline conversions." },
  { n: "03", t: "Account and campaign structure", d: "Funnel-phase differentiation, account ownership, naming conventions. Structure that enables steering and later handover." },
  { n: "04", t: "Pacing strategy and budget architecture", d: "Daily and monthly pacing, budget reserves, seasonal adjustments. Budget as a steerable instrument, not a weekly average." },
  { n: "05", t: "Conversion optimization levers", d: "Landing page architecture, form optimization, A/B test methodology, mobile-first. What belongs reviewed before any budget increase." },
  { n: "06", t: "Negative keyword management and wastage reduction", d: "Reading search-term reports, applying three types of negatives. Discipline that recovers monthly spend." },
  { n: "07", t: "Reporting cadence and escalation logic", d: "Core metrics, frequencies, three escalation stages. Reporting that prepares decisions — not a dashboard for its own sake." },
];

function ChecklistEN() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="METHODICAL CHECKLIST · 2026 EDITION"
          headline={<>Performance marketing setup <em>done methodically.</em></>}
          lead="Structured setup sequence in seven phases — from economic pre-validation through tracking architecture, account structure, and pacing strategy, to conversion levers, negative-keyword management, and reporting cadence. 20 pages, methodologically substantive, free of charge."
          metaStrip={["7 phases · ~20 pages", "PDF · free", "Substance, not a tips list"]}
          breadcrumbs={[{ label: "Start", to: "/en" }, { label: "Tools", to: "/en/tools" }, { label: "Performance Marketing Checklist" }]}
        />

        <Section number="02" kicker="Content" headline={<>Seven phases, <em>methodically ordered.</em></>}>
          <ol className="space-y-4">
            {PHASES.map((p) => (
              <li key={p.n} className="flex gap-5 rounded-lg border border-rule bg-white p-5">
                <span className="font-mono text-xs tracking-wider text-ink-4">{p.n}</span>
                <div>
                  <h3 className="font-display text-lg text-ink">{p.t}</h3>
                  <p className="mt-1 text-[15px] leading-relaxed text-ink-2">{p.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </Section>

        <Section number="03" kicker="Download" headline={<>Methodological <em>access</em> to the checklist.</>} variant="tinted">
          <ChecklistDownloadForm
            locale="en"
            checklistSlug="performance-marketing-setup"
            directDownloadUrl="/static/checklists/performance-marketing-setup-en.pdf"
          />
        </Section>

        <Section number="04" kicker="Methodological note" headline={<>The checklist is <em>no substitute</em> for individual substance.</>}>
          <div className="max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7 text-[16px] leading-relaxed text-ink-2">
            <p>
              This checklist is substantive, but no substitute for individual methodological substance. Every company has its own margin structures, AOV ranges, and conversion mechanics. If you want to discuss the application to your concrete situation: free strategy conversation at{" "}
              <Link to="/en/contact" className="text-cyan-deep underline decoration-cyan/40 underline-offset-4 hover:decoration-cyan-deep">
                /en/contact
              </Link>
              .
            </p>
          </div>
        </Section>

        <Section number="05" kicker="Related tools" headline={<>Three methodical <em>follow-ups.</em></>}>
          <div className="grid gap-5 md:grid-cols-3">
            <Link to="/en/tools/profitability-calculator" className="group rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-cyan">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Tool</p>
              <h3 className="mt-3 font-display text-lg text-ink">Run a viability check first</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-ink-2">CPC thresholds from AOV, margin, CVR. Before the first campaign.</p>
              <span className="mt-4 inline-block font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-cyan-deep">Open calculator →</span>
            </Link>
            <Link to="/en/tools/lighthouse-check" className="group rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-cyan">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Tool</p>
              <h3 className="mt-3 font-display text-lg text-ink">Lighthouse check for your site</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-ink-2">Performance, accessibility, best practices, SEO — in under a minute.</p>
              <span className="mt-4 inline-block font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-cyan-deep">Start audit →</span>
            </Link>
            <Link to="/en/services/performance-marketing" className="group rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-cyan">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Service</p>
              <h3 className="mt-3 font-display text-lg text-ink">Performance marketing services</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-ink-2">When the checklist confirms the need — methodical execution.</p>
              <span className="mt-4 inline-block font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-cyan-deep">View services →</span>
            </Link>
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
